import React, { useContext } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ProductsContext } from '../context/products.context';

const TestNivoBarChart = ({ jobs2 }) => {
  const { jobs, workforce } = useContext(ProductsContext);

  // Map Workforce Development Area (WDA) to MSA
  const msaMapping = {
    "Northwest WDA: Whatcom county": "Bellingham, WA",
    "Olympic Consortium WDA: Kitsap county": "Bremerton-Silverdale, WA",
    "North Central WDA: Grant, Adams counties": "Eastern Washington nonmetropolitan area",
    "Benton- Franklin WDA: Benton, Franklin counties": "Kennewick-Richland, WA",
    "Eastern Washington WDA: Asotin, Whitman, Garfield Counties": "Lewiston, ID-WA",
    "Southwest WDA: Cowlitz, Wahkiakum counties": "Longview, WA",
    "Northwest WDA: Skagit county": "Mount Vernon-Anacortes, WA",
    "Pacific Mountain WDA: Grays Harbor, Lewis, Mason, Pacific, Thurston counties": "Olympia-Tumwater, WA",
    "South Central WDA: Skamania County": "Portland-Vancouver-Hillsboro, OR-WA",
    "Seattle King WDA: King County": "Seattle-Tacoma-Bellevue, WA",
    "Spokane WDA: Spokane County": "Spokane-Spokane Valley, WA",
    "Eastern Washington WDA: Columbia, Walla Walla Counties": "Walla Walla, WA",
    "North Central WDA: Chelan Douglas counties": "Wenatchee, WA",
    "Northwest WDA: Island, San Juan counties": "Western Washington nonmetropolitan area",
    "South Central WDA: Yakima County": "Yakima, WA",
    "North Central WDA: Okanogan County": "Eastern Washington nonmetropolitan area",
    "South Central WDA: Klickitat County": "Portland-Vancouver-Hillsboro, OR-WA",
    "Snohomish WDA: Snohomish County": "Seattle-Tacoma-Bellevue, WA",
    "Olympic Consortium WDA: Clallam, Jefferson counties": "Western Washington nonmetropolitan area",
    "South Central WDA: Kittitas County": "Yakima, WA",
    "Eastern Washington WDA: Stevens, Ferry, Pend Oreille, Lincoln counties": "Eastern Washington nonmetropolitan area",
    "Southwest WDA: Clark County": "Portland-Vancouver-Hillsboro, OR-WA",
    "Tacoma-Pierce WDA: Pierce County": "Seattle-Tacoma-Bellevue, WA",
    "Washington State": "Washington State",
  };

 // Get the region (MSA) based on the workforce
  const regionName = msaMapping[workforce] || "Unknown Region";

  // Build dynamic chart title
  const chartTitle = `Hourly Wage Comparison for ${regionName}`;


  // Destructure fields from new database:
  // - 25thPercentileWage, 50thPercentileWage, 75thPercentileWage
  const {
    onetTitle,
    onetSOC,
    "25thPercentileWage": entryWage,       // Entry
    "50thPercentileWage": medianWage,      // Median
    "75thPercentileWage": experiencedWage, // Experienced
  } = jobs2;

  // Find an aggregated "all-occupations" or "00-0000" record for reference
  const avgHourlyWageAllOccupations =
    jobs.socData.find((e) => e.onetSOC.includes('000000'))?.["50thPercentileWage"] ?? 0;

  // Attempt to find a related occupation in the same major group (xx-0000):
  const tableData = jobs.socData.find((e) =>
    e.onetSOC.includes(onetSOC.substring(0, 2) + '0000')
  );

  // If found, let's store that row's 25th/50th/75th for comparison
  // (replace with your new percentile fields if you want to show a second bar)
  const formattedData = tableData
    ? {
        occupation: tableData.onetTitle,
        entry: tableData["25thPercentileWage"],
        median: tableData["50thPercentileWage"],
        experienced: tableData["75thPercentileWage"],
      }
    : null;

  // Build chart data with your primary (jobs2) plus a secondary (formattedData)
  const data = [
    {
      occupation: onetTitle || 'Loading...',
      entry: entryWage ?? null,
      median: medianWage ?? null,
      experienced: experiencedWage ?? null,
      primary: true, // Mark this as the primary occupation row
    },
    ...(formattedData
      ? [
          {
            occupation: formattedData.occupation,
            entry: formattedData.entry,
            median: formattedData.median,
            experienced: formattedData.experienced,
          },
        ]
      : [])
  ];

  // Custom colors for the bars
  const getBarColors = (bar) => {
    const isPrimary = bar.data.primary === true;
    if (isPrimary) {
      // Primary occupation (Green shades)
      switch (bar.id) {
        case 'entry':
          return '#a7f3d0'; // Light green
        case 'median':
          return '#34d399'; // Medium green
        case 'experienced':
          return '#059669'; // Dark green
        default:
          return '#d4d4d4';
      }
    } else {
      // Secondary occupation (Gray shades)
      switch (bar.id) {
        case 'entry':
          return '#e5e5e5'; // Light gray
        case 'median':
          return '#a3a3a3'; // Medium gray
        case 'experienced':
          return '#737373'; // Dark gray
        default:
          return '#d4d4d4';
      }
    }
  };

  // Format numbers as currency (e.g. $40, $53, etc.)
  const formatCurrency = (value) => {
    if (value == null) return '';
    return `$${Math.round(value).toLocaleString()}`;
  };

  // Format label for % difference vs. previous
  const formatPercent = (value) => {
    const percentage = Math.round(value * 100);
    return `▲${percentage}%`;
  };

  // Utility to break axis labels at word boundaries
  const splitTextOnWords = (text, maxLength) => {
    const words = (text || '').split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach((word) => {
      if ((currentLine + word).length > maxLength) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine += (currentLine ? ' ' : '') + word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }
    return lines;
  };

  // Custom dashed-line symbol for "Livable Wage" legend
  const LivableWageLegendSymbol = ({ x, y, size, fill }) => (
    <line
      x1={x}
      y1={y + size / 2}
      x2={x + size}
      y2={y + size / 2}
      stroke={fill}
      strokeWidth={1}
      strokeDasharray="6, 4"
    />
  );

  return (
    <div className="w-full max-w-[480px] mx-auto z-50">
       <h2 className="text-center text-xs font-bold mb-4 text-zinc-500 border-b border-zinc-200">
        {chartTitle}
      </h2>
<div style={{ height: '200px', width: '420px' }}>
      <ResponsiveBar
        data={data}
        keys={['entry', 'median', 'experienced']}
        indexBy="occupation"
        margin={{ top: 50, right: 50, bottom: 50, left: 0 }}
        padding={0.175}
        layout="vertical"
        colors={getBarColors}
        groupMode="grouped"
        axisTop={null}
        axisRight={null}
        axisLeft={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 0,
          tickRotation: 0,
          legend: '',
          legendPosition: 'middle',
          legendOffset: 32,
          
          renderTick: (props) => {
            const { value, x, y } = props;
            if (!value) return null; // Avoid rendering empty ticks
        
            // Break the text at word boundaries
            const lines = splitTextOnWords(value, 27); // Adjust line length as needed
            return (
              <g transform={`translate(${x},${y + 14})`}>
                {lines.map((line, index) => (
                  <text
                    key={index}
                    x={0}
                    y={index * 15}
                    textAnchor="middle"
                    style={{
                      fill: '#333333',
                      fontSize: '0.75rem',
                      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                    }}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          },
        }}
        enableLabel={true}
        // Show % difference from previous
        label={(bar) => {
          const { entry, median, experienced } = bar.data;
          let value = null;
          if (bar.id === 'experienced' && median != null) {
            value = (experienced - median) / median;
          } else if (bar.id === 'median' && entry != null) {
            value = (median - entry) / entry;
          }
          return value !== null && !isNaN(value) ? formatPercent(value) : '';
        }}
        labelTextColor="#FFFFFF"
        legends={[
          {
            dataFrom: 'keys',
            anchor: 'top-center',
            direction: 'row',
            justify: false,
            translateX: 0,
            translateY: -40,
            itemsSpacing: 2,
            itemWidth: 60,
            itemHeight: 12,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolShape: 'circle',
            symbolSize: 12,
            data: [
              { id: 'entry', label: 'Entry', color: '#a7f3d0' },
              { id: 'median', label: 'Median', color: '#34d399' },
              { id: 'experienced', label: 'Experienced', color: '#059669' },
            ],
          },
          {
            anchor: 'top-right',
            direction: 'row',
            justify: false,
            translateX: 49,
            translateY: -40,
            itemsSpacing: 2,
            itemWidth: 100,
            itemHeight: 12,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 26,
            symbolShape: LivableWageLegendSymbol,
            data: [
              {
                id: 'livableWage',
                label: 'All Jobs Med',
                color: '#a3a3a3',
              },
            ],
          },
          {
            anchor: 'top-right',
            direction: 'row',
            justify: false,
            translateX: -65,
            translateY: -40,
            itemsSpacing: 2,
            itemWidth: 100,
            itemHeight: 12,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 0,
            data: [
              {
                id: 'percentageChange',
                label: '▲ %Change vs Prev',
                color: '#059669',
              },
            ],
          },
        ]}
        innerPadding={2}
        borderRadius={2}
        isInteractive={false}
        // Show dashed line at "all-occupations" average
        gridYValues={[avgHourlyWageAllOccupations]}
        theme={{
          grid: {
            line: {
              stroke: '#a3a3a3',
              strokeWidth: 1,
              strokeDasharray: '6, 4',
            },
          },
        }}
        layers={[
          'grid',
          'axes',
          'markers',
          'legends',
          // 1) Custom dashed line with label for "all occupations" average
          ({ yScale, width }) => {
            const gridLineY = yScale(avgHourlyWageAllOccupations);
            return (
              <>
                {/* Faint background for the label */}
                <rect
                  x={width - 45}
                  y={gridLineY - 14}
                  width={45}
                  height={14}
                  fill="#f4f4f5"
                />
                <line
                  x1={0}
                  x2={width}
                  y1={gridLineY}
                  y2={gridLineY}
                  stroke="#a3a3a3"
                  strokeWidth={1}
                  strokeDasharray="6, 4"
                />
                <text
                  x={width}
                  y={gridLineY - 4}
                  textAnchor="end"
                  style={{ fill: '#737373', fontSize: 10 }}
                >
                  <tspan x={width - 3} dy="0">
                    {formatCurrency(avgHourlyWageAllOccupations)}
                  </tspan>
                </text>
              </>
            );
          },
          // 2) Draw currency labels above each bar
          ({ bars }) => {
            return bars.map((bar) => (
              <g key={bar.key}>
                <rect
                  x={bar.x + bar.width / 2 - 22}
                  y={bar.y - 12}
                  width={45}
                  height={12}
                  fill="white"
                />
                <text
                  x={bar.x + bar.width / 2}
                  y={bar.y - 4}
                  textAnchor="middle"
                  style={{
                    fill:
                      bar.color === '#a7f3d0' ||
                      bar.color === '#34d399' ||
                      bar.color === '#059669'
                        ? '#059669'
                        : '#737373',
                    fontSize: 10,
                    fontWeight: 'bold',
                  }}
                >
                  {bar.data.value !== null ? formatCurrency(bar.data.value) : ''}
                </text>
              </g>
            ));
          },
         
          'bars',// 3) Baseline at 0
          ({ yScale }) => {
            const zeroPosition = yScale(0);
            return (
              <line
                x1={0}
                x2="100%"
                y1={zeroPosition}
                y2={zeroPosition}
                stroke="#525252"
                strokeWidth={1}
              />
            );
          },
        ]}
      />
    </div> {/* Dynamic Chart Title */}
      </div>
  );
};

export default TestNivoBarChart;